import React, { useCallback, useEffect, useState } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';

import { Button } from '../../../components/button/Button';

import { Container, EventCard, NoneEvents, Title } from './OrderEventsDialog.styled';
import { IEvent } from '../../../interfaces/IEvent';
import { useToastr } from '../../../hooks/useToastr';
import { useOrderEventsService } from '../../../services/useOrderEventService';
import moment from 'moment';
import { BackdropCustom } from '../../../components/backdrop/Backdrop';

interface IOrderEventsDialogProps {
  dialogOpen: boolean;
  orderId: string;
  handleCloseDialog: () => void;
  handleOpenDialog: () => void;
}

const OrderEventsDialog: React.FC<IOrderEventsDialogProps> = ({
  dialogOpen,
  orderId,
  handleCloseDialog,
  handleOpenDialog,
}) => {
  const [events, setEvents] = useState<IEvent[]>([]);
  const [event, setEvent] = useState<IEvent>();
  const [askEmailUnlockMessage, setAskEmailUnlockMessage] = useState('');
  const [messageError, setMessageError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isEmailDialogOpen, setIsEmailDialogOpen] = useState(false);

  const toastr = useToastr();

  const { listOrderEvent, askOrderApproval } = useOrderEventsService();

  const aksEmailUnlockOrder = async () => {
    if (!askEmailUnlockMessage) {
      setMessageError(true);
      return;
    }

    if (!event) {
      toastr.error('Ocorreu um erro na solicitação. Tente novamente');
      return;
    }
    setLoading(true);
    await askOrderApproval(event.id, askEmailUnlockMessage)
      .then(() => {
        toastr.success('Solicitação efetuada com sucesso');
        handleListEvents();
      })
      .catch(() => {
        toastr.error('Ocorreu um erro ao realizar a solicitação.');
      })
      .finally(() => {
        setLoading(true);
        setIsEmailDialogOpen(false);
        setMessageError(false);
      });
  };

  const handleListEvents = useCallback(async () => {
    setLoading(true);
    await listOrderEvent(orderId, true)
      .then(response => {
        setEvents(response);
      })
      .catch(error => {
        toastr.error(error?.message || 'Contate a equipe de suporte');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [orderId]);

  useEffect(() => {
    if (dialogOpen) {
      setMessageError(false);
      setAskEmailUnlockMessage('');
      handleListEvents();
    }
  }, [dialogOpen]);

  return (
    <>
      {loading ? (
        <BackdropCustom />
      ) : (
        <Container
          open={dialogOpen}
          onClose={handleOpenDialog}
          sx={{
            '& .MuiDialog-container': {
              width: '100vw',
              justifyContent: 'center',
              alignItems: 'center',
            },
          }}
        >
          <DialogContent>
            <Title>Histórico de alteração dos status</Title>

            {events && events.length > 0 ? (
              events.map(event => {
                return (
                  <EventCard key={event.id}>
                    <div>
                      <strong>
                        {event.orderStatus && event.orderStatus?.description
                          ? event.orderStatus.description
                          : 'Status não informado'}
                      </strong>
                      <span>{moment(event.created_at).format('DD/MM/YYYY HH:mm')}</span>
                    </div>
                    <span>
                      {event.message.split('<br />').map((part, index) => (
                        <React.Fragment key={index}>
                          {part}
                          {index < event.message.split('<br />').length - 1 && <br />}
                        </React.Fragment>
                      ))}
                    </span>
                    {event.emailApprovalStatus === 'BLOQUEADO' && (
                      <div style={{ marginTop: 10 }}>
                        <Button
                          onClick={() => {
                            setEvent(event);
                            setIsEmailDialogOpen(true);
                          }}
                        >
                          Habilitar E-mail de Desbloqueio
                        </Button>
                      </div>
                    )}
                  </EventCard>
                );
              })
            ) : (
              <NoneEvents>
                <span>Nenhum evento para listar...</span>
              </NoneEvents>
            )}
          </DialogContent>
          <DialogActions sx={{ marginRight: '10px' }}>
            <Button color="info" onClick={handleCloseDialog} sx={{ textTransform: 'none' }}>
              Fechar
            </Button>
          </DialogActions>
          <Dialog open={isEmailDialogOpen} fullWidth maxWidth="xs">
            <DialogTitle>Motivo do Desbloqueio</DialogTitle>
            <DialogContent>
              <TextField
                sx={{ width: '100%', marginTop: '5px' }}
                onChange={e => setAskEmailUnlockMessage(e.target.value)}
                multiline
                rows={4}
                size="small"
                label="Motivo de desbloqueio"
                error={Boolean(messageError)}
                helperText={messageError && 'Justificativa não informada'}
              />
            </DialogContent>
            <DialogActions>
              <Button color="inherit" onClick={() => setIsEmailDialogOpen(false)}>
                Fechar
              </Button>
              <Button onClick={() => aksEmailUnlockOrder()}>Confirmar</Button>
            </DialogActions>
          </Dialog>
        </Container>
      )}
    </>
  );
};

export { OrderEventsDialog };
